<template>
  <div id="main-content">
    <div class="content-page-banner">
      <div class="banner-content">
        <h1>{{ $route.meta.title }}</h1>
        <div class="underline"></div>
        <template v-if="!loading">
          <div v-if="isBuildCRM()">
            <p>
              Your member card identifies you as a member of a special community – the Defence Health family. Use it to claim on-the spot at most extras providers. You’ll also need
              to present your card if you or someone covered by your policy goes to hospital. So keep it safe.
            </p>
            <p>
              Requests for new Member cards will be sent to your
              <span>nominated postal address</span>
              . So make sure that is up to date before you request a new card.
            </p>
            <p>Order today and we'll get the requested cards to you in the next 10 business days.</p>
          </div>
          <div v-else v-html="content.info"></div>
        </template>
      </div>
    </div>

    <Alert v-if="apiFail || recentOrderExists" :message="highLevelMessage" :alertType="apiFail ? 'alert error' : 'info'" />

    <div id="multi-step-form" :class="currentPage === pages.REVIEW_AND_SUBMIT ? 'last-step' : null">
      <!-- Form navigation -->
      <MultiStepFormNavigation :currentPage="currentPage" :links="navLinks" @linkClicked="updateCurrentStep" />

      <!-- Form contents -->
      <div id="form-contents">
        <!-- Initial form loading state -->
        <ApiLoading v-if="loading || apiFail" :apiError="apiFail" class="api-loading" isMemberPortal />

        <!-- Step 1: Order member card page -->
        <StepOrderMemberCard
          v-else-if="currentPage == pages.ORDER_MEMBER_CARDS"
          :numberOfCards="numberOfCards"
          :postalAddress="postalAddress"
          @select="updateNumberOfCards"
          :navDisabled="v$.numberOfCards.$invalid || v$.numberOfCards.$error"
          :handleNextButton="handleNextButton"
          :hideAddress="keepPrivate"
        />

        <!-- Step 2: Review and submit page -->
        <StepReviewAndSubmit
          v-else-if="currentPage == pages.REVIEW_AND_SUBMIT"
          :numberOfCards="numberOfCards"
          :postalAddress="postalAddress"
          :handleBackButton="handleBackButton"
          :handleSubmit="submitOrder"
          :loading="loading"
          :apiError="apiFail"
          :hideAddress="keepPrivate"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { mapGetters, mapMutations } from 'vuex';
import { getQueryString } from '@/utils/UrlUtils';
import { ErrorStrings } from '@general/js/constants';
import { getCurrentDate } from '@general/js/utils';
import { AccessLevel } from '@/enums';
import { VUEX_GENERAL, VUEX_GENERATE_MEMBER_CARD } from '@/constants/VuexConstants';
import { Alert, ApiLoading, MultiStepFormNavigation } from '@/components';
import GenerateMemberCardsServices from '@/services/GenerateMemberCards';
import { GENERATE_MEMBER_CARD_REQUEST_SUCCESS } from '@/router/routeNames';
import { setNavigationState } from '@/router/navigationState';
import { formatDateToDisplay, getFullAddressText, isBuildCRM, isEmptyString, replaceToBaseUrlPath, convertDateToDDMMYYYYWithSlash, checkWithinXDays } from '@/utils';
import { StepOrderMemberCard, StepReviewAndSubmit } from './RequestFormSteps';

const isCRM = isBuildCRM();

export default {
  setup() { return { v$: useVuelidate() }; },

  name: 'GenerateMemberCards',

  components: {
    Alert,
    ApiLoading,
    MultiStepFormNavigation,
    StepOrderMemberCard,
    StepReviewAndSubmit,
  },

  data() {
    return {
      pages: {
        ORDER_MEMBER_CARDS: 1,
        REVIEW_AND_SUBMIT: 2,
      },
      loading: false, // Default
      apiFail: false,
      recentOrderExists: false, // Default
      contactId: '',
      formName: 'Generate member card',
      CRM_PAGES: {
        LANDING: 'GenerateMemberCards',
        RESULT: 'SuccessRequestNewCards',
      },
      numberOfDays: 10,
      postalAddressAccessLevel: null,
      keepPrivate: false,
      serviceAgentName: null,
      CRM_STATES: {
        1: 'ACT',
        2: 'NSW',
        3: 'NT',
        4: 'QLD',
        5: 'SA',
        6: 'TAS',
        7: 'VIC',
        8: 'WA',
      },
      content: {},
      mostRecentAddress: true,
    };
  },

  validations: {
    numberOfCards: {
      required,
    },
  },

  beforeMount() {
    let currentStep = Number(getQueryString('step'));
    if (currentStep) {
      currentStep = Number.isNaN(currentStep) ? 1 : currentStep;
      this.updateCurrentPage(currentStep);
    } else {
      this.updateCurrentPage(1);
    }
  },

  async mounted() {
    // Fetch the first set of API data
    this.fetchDataForStep(this.currentPage);

    this.getCRMMembershipId();
    this.getCRMPersonCode();
    this.getCRMPostalAddress();

    if (!isCRM) {
      await this.getReferenceData();
      await this.getRecentOrder();
      // Only listen to the pop state event if this is not running in CRM.
      // This will add a listener to the window back and forward button, so that it can call a callback function to perform some operation when it is called.
      window.addEventListener('popstate', this.navigationCallback, false);
    }
  },

  beforeUnmount() {
    this.resetState();

    // Only remove the listener if not running in CRM mode.
    if (!isCRM) {
      // Remove the listener for the back and forward browser buttons.
      window.removeEventListener('popstate', this.navigationCallback);
    }
  },

  computed: {
    ...mapGetters({
      personCode: VUEX_GENERAL.GETTERS.getCurrentPersonCode,
      memberNumber: VUEX_GENERAL.GETTERS.getCurrentPolicyNumber,
      members: VUEX_GENERAL.GETTERS.getMembers,
      currentMembership: VUEX_GENERAL.GETTERS.getCurrentMembership,
    }),

    ...mapGetters('GenerateMemberCards', {
      numberOfCards: VUEX_GENERATE_MEMBER_CARD.GETTERS.getNumberOfCards,
      postalAddress: VUEX_GENERATE_MEMBER_CARD.GETTERS.getPostalAddress,
      recentOrder: VUEX_GENERATE_MEMBER_CARD.GETTERS.getRecentOrder,
      currentPage: VUEX_GENERATE_MEMBER_CARD.GETTERS.getCurrentPage,
    }),

    /**
     * Computes whether the link is disabled using the validation that is completed in Vuelidate.
     */
    navLinks() {
      let isStepOneDisabled;
      let stepOneInvalid;

      if (this.currentPage === this.pages.REVIEW_AND_SUBMIT && this.loading) {
        /**
         * While loading api,in review page, before navigate to success page, all side navigation links need to be disabled.
         * Setting everyting true as below will return each links' disabled key value to be true hence return disabled nav links.
         */
        isStepOneDisabled = true;
        stepOneInvalid = true;
      } else {
        isStepOneDisabled = false;
        stepOneInvalid = this.v$.numberOfCards.$invalid || this.v$.numberOfCards.$error;
      }

      /**
       * The first link is always enabled unless current page is last step and request card api is called.
       * The consecutive links are enabled if the previous page is valid, if the input of the previous page is no longer valid, then all links besides the first one is disabled.
       * This may need to be modified as currently as soon as valid input is detected, it will allow the link to be enabled.
       */
      return [
        { linkTitle: 'Order member cards', pages: [1], disabled: isStepOneDisabled },
        { linkTitle: 'Review and submit', pages: [2], disabled: stepOneInvalid },
      ];
    },

    /**
     * Message for high level alert box.
     */
    highLevelMessage() {
      // TODO: Need to update the message depend on the state
      let message = '';

      if (this.recentOrderExists) {
        // convert 'YYYY-MM-DD' to 'DD/MM/YYYY' and then format date to display DD Month Year.
        message = `${this.recentOrder.numberOfCards} card(s) were ordered on ${this.formatDateToDisplay(
          convertDateToDDMMYYYYWithSlash(this.recentOrder.issueDate),
        )}. Please allow up to 10 working days for delivery.`;
      }

      // If api fails, remove recent order message and replace with error message. Error messages will be different depending on current page
      if (this.apiFail) {
        if (this.currentPage === this.pages.ORDER_MEMBER_CARDS) {
          message = ErrorStrings.GENERIC_ERROR;
        } else {
          // prettier-ignore
          message = ErrorStrings.SUBMIT_API_ERROR;
        }
      }

      return message;
    },
  },

  methods: {
    ...mapMutations({
      updateMembershipId: VUEX_GENERAL.MUTATIONS.saveThisPolicyNumber,
      updateCurrentMemberDetails: VUEX_GENERAL.MUTATIONS.updateCurrentMemberDetails,
    }),
    ...mapMutations('GenerateMemberCards', {
      resetState: VUEX_GENERATE_MEMBER_CARD.MUTATIONS.resetState,
      updateNumberOfCards: VUEX_GENERATE_MEMBER_CARD.MUTATIONS.updateNumberOfCards,
      updatePostalAddress: VUEX_GENERATE_MEMBER_CARD.MUTATIONS.updatePostalAddress,
      updateRecentOrder: VUEX_GENERATE_MEMBER_CARD.MUTATIONS.updateRecentOrder,
      updateCurrentPage: VUEX_GENERATE_MEMBER_CARD.MUTATIONS.updateCurrentPage,
    }),

    getCRMMembershipId() {
      if (isCRM) {
        if (window.parent.getContext) {
          const context = window.parent.getContext;
          if (context.getAttribute('dhl_membershipid')) {
            this.updateMembershipId(context.getAttribute('dhl_membershipid').getValue());
          }
        }
      }
    },

    getCRMPersonCode() {
      if (isCRM) {
        if (window.parent.getContext) {
          const context = window.parent.getContext;
          if (context.getAttribute('dhl_memberid')) {
            const memberDetails = {
              personCode: context.getAttribute('dhl_memberid').getValue(),
            };
            this.updateCurrentMemberDetails(memberDetails);
          }
        }
      }
    },

    /**
     * Gets the postal address type for CRM to use for submit request of new cards.
     * If the current person is a policy holder, use "M", otherwise, "P"
     */
    getCRMPostalAddress() {
      if (isCRM) {
        if (window.parent.getContext) {
          const context = window.parent.getContext;
          if (context.getAttribute('dhl_relationship') && context.getAttribute('dhl_relationship').getValue().length > 0) {
            if (context.getAttribute('dhl_relationship').getValue()[0].name.toLowerCase() === AccessLevel.POLICY_HOLDER_ACCESS.replace(' access', '')) {
              this.postalAddressAccessLevel = 'M';
            } else {
              this.postalAddressAccessLevel = 'P';
            }
          }
        }
      }
    },

    /**
     * Gets the current service agent name in CRM (the Consultant currently logged in)
     */
    getCRMServiceAgentName() {
      if (isCRM) {
        if (window.parent.Xrm && window.parent.Xrm.Utility) {
          this.serviceAgentName = window.parent.Xrm.Utility.getGlobalContext().userSettings.userName;
        }
      }
    },

    /**
     * Handles when a state is popped off the history, this can include going backwards and forwards.
     */
    navigationCallback() {
      // Update the current page using the step query
      const page = this.$route.query.step ? parseInt(this.$route.query.step, 10) : 1;
      if (this.shouldUpdatePage(page)) {
        this.updateCurrentPage(page);
      } else {
        replaceToBaseUrlPath(this.$route);
      }
    },

    /**
     * emit data to parent for page navigation in CRM.
     *
     * @param {string} targetPageName name of target page. it should be one of `PAGES` object value.
     * @param {object} routeParams by default is empty object. if given, it should be object. this object will be used like route param in non CRM build.
     */
    crmNavigatePage(targetPageName, routeParams = {}) {
      window.scrollTo(0, 0);
      this.$emit('crmNavigatePage', targetPageName, routeParams);
    },

    updateCurrentStep(pageNumber) {
      this.updateCurrentPage(pageNumber);

      // Determine whether the fetch data needs to occur. Only fetch data if the page is moving forwards, and not when it's going backwards.
      if (this.checkReferenceData(pageNumber)) {
        this.fetchDataForStep(this.currentPage);
      }
    },

    /**
     * Determine whether the page should be updated to the next page. Uses whether the previous step form is invalid to allow it to proceed.
     */
    shouldUpdatePage(currentPage) {
      // Need to determine whether the updating of the current page should happen depending on the previous
      const step1Invalid = this.v$.numberOfCards.$invalid || this.v$.numberOfCards.$error;
      let updatePage = false;

      if (currentPage === this.pages.ORDER_MEMBER_CARDS) {
        updatePage = true;
        return updatePage;
      }

      updatePage = !step1Invalid;
      return updatePage;
    },

    /**
     * Determines the display label for the Number of cards to be sent dropdown.
     */
    getNumberDropdownDisplayValue(selectedNumber) {
      return selectedNumber !== '' ? selectedNumber : 'Select number of cards';
    },

    /**
     * Handles the Back button being pressed.
     */
    handleBackButton() {
      this.updateCurrentPage(this.currentPage - 1);

      // Check if the data will need to be refetched, this is primarily for the refresh scenario
      if (this.checkReferenceData(this.currentPage)) {
        this.fetchDataForStep(this.currentPage);
      }
    },

    /**
     * Check to determine if the reference data for the page is already downloaded. This will be used when clicking backwards where it may need to download data as it should not need to fetch the data all the time as long as the reference already exists.
     */
    checkReferenceData(currentPage) {
      if (currentPage === this.pages.REVIEW_AND_SUBMIT) {
        return this.postalAddress.length === 0;
      }
      return false;
    },

    /**
     * Handles the Next button being pressed.
     */
    handleNextButton() {
      this.updateCurrentPage(this.currentPage + 1);
      this.fetchDataForStep(this.currentPage);
    },

    /**
     * Handles the Request Cards button being pressed.
     */
    async submitOrder() {
      this.loading = true;
      this.apiFail = false;
      const responseData = await this.submitMemberCardRequest();

      if (isCRM) {
        if (responseData.message) {
          const message = responseData.message;
          if (message.includes('Successfully generated member card')) {
            this.crmNavigatePage(this.CRM_PAGES.RESULT);
          } else {
            this.apiFail = true;
          }
        } else {
          this.apiFail = true;
        }
      } else if (!isCRM && responseData.orderSuccessFlag) {
        this.updateCurrentPage(1);
        setNavigationState({ content: [this.content.confirmationInformationText, this.content.relatedArticles] });
        this.$router.push({ name: GENERATE_MEMBER_CARD_REQUEST_SUCCESS });
      } else {
        this.apiFail = true;
      }
    },

    /**
     * Handle the API call of submit the member card detail.
     */
    async submitMemberCardRequest() {
      try {
        this.loading = true;
        this.apiFail = false;

        let data;
        if (!isCRM) {
          data = await GenerateMemberCardsServices.submitNewCardOrder(this.memberNumber, Number(this.numberOfCards), this.postalAddressAccessLevel, getCurrentDate());
        } else {
          data = await GenerateMemberCardsServices.submitNewCardOrder(
            this.memberNumber,
            this.personCode,
            Number(this.numberOfCards),
            this.postalAddressAccessLevel,
            getCurrentDate(),
            this.serviceAgentName,
          );
        }
        return data;
      } catch (error) {
        this.apiFail = true;
        return false;
      } finally {
        this.loading = false;
        this.recentOrderExists = false;
      }
    },

    /**
     * Fetch required API data for the given page.
     *
     * NOTE: Not fetching any data ahead of the required step otherwise if the user requests a page refresh, then no data may be returned for the given page.
     */
    async fetchDataForStep(currentPage) {
      try {
        // Display the loading screen
        this.loading = true;
        this.apiFail = false;

        if (currentPage === this.pages.ORDER_MEMBER_CARDS) {
          if (!isCRM) {
            const postalAddressData = await GenerateMemberCardsServices.getPostalAddress(this.memberNumber, this.mostRecentAddress);
            this.postalAddressAccessLevel = postalAddressData.addressList[0].preferredAddress;
            this.keepPrivate = postalAddressData.addressList[0].keepPrivate;

            if (!this.keepPrivate) {
              const fullPostalAddress = getFullAddressText(
                postalAddressData.addressList[0].addressLine1,
                postalAddressData.addressList[0].addressLine2,
                postalAddressData.addressList[0].addressLine3,
                postalAddressData.addressList[0].suburb,
                postalAddressData.addressList[0].state,
                postalAddressData.addressList[0].postcode,
              );
              this.updatePostalAddress(fullPostalAddress);
            }
          } else {
            this.getCRMMembershipId();
            this.getCRMPersonCode();
            this.getCRMPostalAddress();
            this.getCRMServiceAgentName();

            const recentOrder = await GenerateMemberCardsServices.getLastOrder(this.memberNumber);
            this.updateRecentOrder(recentOrder);

            this.recentOrderExists = checkWithinXDays(recentOrder.issueDate, this.numberOfDays); // check issue date within 10 days of current date

            const postalAddress = await GenerateMemberCardsServices.getPostalAddress(this.memberNumber, this.personCode);

            let fullPostalAddress;
            // if postal and residential address is the different, take postal address
            const differentPostalAddress = !!postalAddress.dhl_addresslineonepostal;
            if (differentPostalAddress) {
                fullPostalAddress = getFullAddressText(
                  postalAddress.dhl_addresslineonepostal,
                  postalAddress.dhl_addresslinetwopostal ?? '',
                  postalAddress.dhl_addresslinethreepostal ?? '',
                  postalAddress.dhl_suburb,
                  this.CRM_STATES[postalAddress.dhl_state],
                  postalAddress.dhl_postcode,
                );
            } else {
              // take the residential address is postal and residential are the same
              const residentialAddress = postalAddress.dhl_Membership;
              fullPostalAddress = getFullAddressText(
                residentialAddress.dhl_addresslineoneresidential,
                residentialAddress.dhl_addresslinetworesidential ?? '',
                residentialAddress.dhl_addresslinethreeresidential ?? '',
                residentialAddress.dhl_suburbresidential,
                this.CRM_STATES[residentialAddress.dhl_state],
                residentialAddress.dhl_postcoderesidential,
              );
            }

            this.updatePostalAddress(fullPostalAddress);
          }
        }
      } catch (error) {
        this.apiFail = true;

        if (error.message) {
          logger.error(error.message);
        } else {
          logger.error(error);
        }
      } finally {
        // Remove the loading screen
        this.loading = false;
      }
    },

    /**
     * Fetch the recent card order for the logged in member.
     */
    async getRecentOrder() {
      this.apiFail = false;
      this.apiLoading = true;

      try {
        const memberCardData = await GenerateMemberCardsServices.getLastOrder(this.memberNumber);

        // Check if recentOrder data return with both issueDate and numberOfCards returned
        if (memberCardData && !this.isEmptyString(memberCardData.issueDate) && !this.isEmptyString(memberCardData.numberOfCards)) {
          this.updateRecentOrder(memberCardData);
          this.recentOrderExists = checkWithinXDays(memberCardData.issueDate, this.numberOfDays); // check issue date within 10 days of current date
        }
      } catch (error) {
        logger.error(error);
        this.apiFail = true;
      } finally {
        this.apiLoading = false;
      }
    },

    /**
     * Fetch the reference data for the CMS page.
     * This will be used to display the updatable content.
     */
    async getReferenceData() {
      this.apiFail = false;
      this.apiLoading = true;

      try {
        const refData = await GenerateMemberCardsServices.getReferenceData();
        this.content = {
          info: refData?.memberCardInformationText?.informationText,
          confirmationInformationText: refData?.confirmationInformationText?.informationText,
          relatedArticles: [refData?.relatedArticle1, refData?.relatedArticle2, refData?.relatedArticle3],
        };
      } catch (error) {
        logger.error(error);
        this.apiFail = true;
      } finally {
        this.apiLoading = false;
      }
    },

    // Bind the imported methods to the component
    formatDateToDisplay,
    isEmptyString,
    isBuildCRM,
  },
};
</script>

<style lang="scss" scoped>
.api-loading {
  margin-bottom: spacer(5);
}

.required-indicator {
  font-size: 0.64rem;
}
</style>
